import { useEffect, useMemo, useState } from 'react';

import { RecentFilterMode, RecentJob } from '../../components/molecules/RecentJobs';
import { columnSelectionItems } from '../../constants/service.constant';
import { useGetServicePaginatedData } from '../../queries/service-query';
import { formatFriendlyDate } from '../../utils/common';
import { getRecentlyAccessedJobs, formatRecentJobTime } from '../../utils/recent-jobs';

// Fields the panel renders by date. They default to unselected in
// `columnSelectionItems`, so without forcing them on the BFF omits them from the
// response and `new Date(undefined)` renders "Invalid Date" for every row.
const RECENT_JOB_DATE_FIELDS = new Set(['createdAt', 'updatedAt']);

export interface UseRecentJobsReturn {
  recentJobs: RecentJob[];
  recentJobsAnchor: HTMLElement | null;
  recentFilter: RecentFilterMode;
  setRecentJobsAnchor: (el: HTMLElement | null) => void;
  setRecentFilter: (mode: RecentFilterMode) => void;
}

export const useRecentJobs = (canViewCreated = true): UseRecentJobsReturn => {
  const [recentJobsAnchor, setRecentJobsAnchor] = useState<HTMLElement | null>(null);
  const [recentFilter, setRecentFilter] = useState<RecentFilterMode>('accessed');

  // Coerce away a 'created' selection the user can no longer view (e.g. the
  // create permission was lost mid-session). Driving the query, the row mapping
  // and the returned filter off this value keeps the data and the panel's
  // displayed selection in agreement — a UI-only clamp would leave the query
  // still sorting/showing created jobs under an "Accessed" toggle.
  const effectiveFilter: RecentFilterMode =
    !canViewCreated && recentFilter === 'created' ? 'accessed' : recentFilter;

  const recentSortField = effectiveFilter === 'created' ? 'createdAt' : 'updatedAt';

  // Force the date columns on so the BFF returns createdAt/updatedAt — they are
  // unselected in the default grid column set, which is what left the Created /
  // Modified tabs showing "Invalid Date". Memoised so the query key stays stable.
  const recentColumnSelection = useMemo(
    () =>
      columnSelectionItems.map((group) => ({
        ...group,
        value: group.value.map((col) =>
          RECENT_JOB_DATE_FIELDS.has(col.field) ? { ...col, value: true } : col
        ),
      })),
    []
  );

  const recentQuery = useGetServicePaginatedData(
    {
      filterModel: { items: [] },
      page: 0,
      pageSize: 25,
      sortModel: [{ field: recentSortField, sort: 'desc' }],
    },
    recentColumnSelection
  );

  const { refetch: refetchRecent } = recentQuery;
  useEffect(() => {
    if (effectiveFilter !== 'accessed' && Boolean(recentJobsAnchor)) {
      refetchRecent();
    }
  }, [effectiveFilter, recentJobsAnchor, refetchRecent]);

  const recentJobs: RecentJob[] = useMemo(() => {
    if (effectiveFilter === 'accessed') {
      return getRecentlyAccessedJobs().map((j) => ({
        id: j.id,
        jobId: j.jobId,
        client: j.client,
        location: j.location,
        time: formatRecentJobTime(j.accessedAt),
      }));
    }
    const apiData = recentQuery.data?.data ?? [];
    return apiData.map((row) => ({
      id: row.id,
      jobId: `JOB-${row.id}`,
      client: row.clientHierarchy ?? '',
      location: row.locationName ?? '',
      time: formatFriendlyDate(effectiveFilter === 'created' ? row.createdAt : row.updatedAt),
    }));
  }, [effectiveFilter, recentQuery.data]);

  return {
    recentJobs,
    recentJobsAnchor,
    recentFilter: effectiveFilter,
    setRecentJobsAnchor,
    setRecentFilter,
  };
};
